import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractForm } from '../abstract-form.model';
import { ErzeugungsanlageConfig } from '../erzeugungsanlage.config';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Tranche } from '../../../domain/model/tranche.model';
import { FormGroupConfig } from '../form-config.model';
import { MarktlokationIdValidator } from '../validator/marktlokation-id.validator';
import { EicCodeValidator } from '../validator/eic-code.validator';
import { PredicateValidator } from '../validator/predicate.validator';
import { MarktpartnerValidator } from '../validator/marktpartner.validator';
import { Feld } from '../../../domain/model/feld.enum';
import { TrancheGroesseValidator } from '../validator/trancheGroesseValidator';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    selector: 'redispatch-tranche-sub-form',
    templateUrl: './tranche-sub-form.component.html',
    styleUrls: [
        './tranche-sub-form.component.scss',
    ],
    standalone: false,
})
export class TrancheSubFormComponent extends AbstractForm<Tranche> implements OnInit, OnDestroy {

    public static readonly SHARED_MARKTLOKATION_VALIDATOR = MarktlokationIdValidator.validate();
    public static readonly SHARED_BILANZKREIS_VALIDATOR = EicCodeValidator.validate();
    public static readonly SHARED_LIEFERANT_VALIDATOR = MarktpartnerValidator.validateSingle();

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;

    @Input({ required: true }) disabled: boolean;
    @Input({ required: true }) readonly: boolean;

    public readonly Feld = Feld;

    private onDestroy$ = new Subject<void>();

    constructor() {
        super(inject(UntypedFormBuilder));
    }

    get formGroupConfig(): FormGroupConfig<Tranche> {
        return {
            marktlokation: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                [
                    TrancheSubFormComponent.SHARED_MARKTLOKATION_VALIDATOR,
                    PredicateValidator.conditionalValidator(() => this.hasAnyTrancheValue, Validators.required),
                ],
            ],
            bilanzkreis: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                [
                    TrancheSubFormComponent.SHARED_BILANZKREIS_VALIDATOR,
                    PredicateValidator.conditionalValidator(() => this.hasAnyTrancheValue, Validators.required),
                ],
            ],
            lieferant: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                [
                    TrancheSubFormComponent.SHARED_LIEFERANT_VALIDATOR,
                    PredicateValidator.conditionalValidator(() => this.hasAnyTrancheValue, Validators.required),
                ],
            ],
            groesse: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                [
                    PredicateValidator.conditionalValidator(() => this.hasAnyTrancheValue, Validators.required),
                    ...TrancheGroesseValidator.validator2,
                ],
            ],
        };
    }

    get hasAnyTrancheValue(): boolean {
        return !!this.f.marktlokation.value || !!this.f.bilanzkreis.value || !!this.f.lieferant.value || !!this.f.groesse.value;
    }

    ngOnInit(): void {
        this.form.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(_value => {
                this.f.marktlokation.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                this.f.bilanzkreis.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                this.f.lieferant.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                this.f.groesse.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    public sanitize(tranche: Tranche): Tranche {
        return tranche;
    }

}
